// MACHINERY V2 (Stroje) — the chrome's SHARED PIECES (M2.16a).
//
// >>> WHY THIS FILE EXISTS. <<< The phone drawer (MobileDrawer.tsx) offers the
// same log-out, the same language switch and the same identity block the
// desktop nav's menus do. All three are short, and all three are exactly the
// kind of short thing that must not exist twice: the log-out SEQUENCE mirrors
// the live app's (Header.tsx) and drifting one copy of it would leave a session
// half-ended; the language switch has a deliberate silent-failure story that a
// second copy would forget; and the identity derivation carries an M2.12
// obligation (below) that must not need applying in two places.
//
// EXTRACTED, NOT REWRITTEN. The log-out body is the shipped one, moved verbatim
// from ChromeRightMenus.tsx; the language body is ChromeLangMenu.tsx's; the
// identity derivation is ChromeRightMenus.tsx's. Those files now call these
// hooks.
//
// THE CHROME'S TWO OPTION TABLES LIVE HERE TOO — language and palette — because
// this file is where the desktop menus and the phone drawer meet, and an option
// list offered on two surfaces from two arrays is a list that will differ.
// The language table is STRONGLY TYPED for one more reason: it was forked into
// MobileDrawer.tsx as `Record<string, string>`, which loses the exhaustiveness
// check, so a fourth SupportedLanguage would fail the build on the desktop menu
// and fall through to an uppercased raw code on the phone — a silent
// half-translation on exactly the surface nobody tests in four languages.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import accountContext from "../../../../account/accountContext";
import { refreshAccount } from "../../../../account/accountSlice";
import { User } from "../../../../account/models/AccountModels";
import { buildAccountSignInWithReturn } from "../../../../account/routes";
import i18n from "../../../../core/i18n";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation, { SupportedLanguage } from "../../../../shared/hooks/useTranslation";
import { CustomerSettingsState } from "../../../settings/models/models";
import customersApi from "../../../shared/api/customersApi";
import { MachineryV2Palette } from "../machineryV2UiSlice";

/** Two-letter menu codes. `Record<SupportedLanguage, …>` is the point: a new
 *  language is a compile error here rather than a raw code on one surface. */
export const LANG_CODES: Record<SupportedLanguage, string> = { sk: "SK", en: "EN", cs: "CZ" };
export const LANG_NAMES: Record<SupportedLanguage, string> = {
  sk: "Slovenčina",
  en: "English",
  cs: "Čeština",
};

/** INTERIM, PENDING M2.12 REVIEW — the full standing note is at the desktop
 *  menu's PaletteSection (ChromeRightMenus.tsx). The list is here so the phone
 *  drawer offers the SAME two options: `palette` drives the root class on every
 *  v2 page, and below 768 the desktop menu that used to be its only dispatcher
 *  never mounts. Do not build more UI on top of it before M2.12. */
export const PALETTE_OPTIONS: { id: MachineryV2Palette; labelKey: string }[] = [
  { id: "claude", labelKey: "myMachinery:v2.palette.claude" },
  { id: "machineid", labelKey: "myMachinery:v2.palette.machineid" },
];

/** The live app's log-out sequence, unchanged: clear the context, drop the
 *  account from the store, then send the user to sign-in with a return URL so
 *  logging back in lands them where they were. */
export const useChromeLogout = (): (() => void) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  return () => {
    accountContext.logout();
    dispatch(refreshAccount(null));
    navigate(
      buildAccountSignInWithReturn(
        `${location.pathname}${location.search}${location.hash}`
      )
    );
  };
};

export interface ChromeLanguage {
  current: SupportedLanguage;
  languages: SupportedLanguage[];
  select: (lng: SupportedLanguage) => void;
}

/**
 * Mirrors LanguageSwitcher.tsx: change the language locally, then persist it
 * best-effort. The persist failure is SWALLOWED on purpose — localStorage
 * (i18nextLng) is the fallback, exactly as the legacy switcher treats it, so a
 * server round trip must never block a language the user already sees.
 *
 * >>> IT TALKS TO THE i18n INSTANCE, NOT TO useTranslation. <<< It used to call
 * `useTranslation(["layout", "common"])` purely to reach `i18n` through it —
 * which handed EVERY consumer a Suspense dependency on two namespaces it may
 * have no text from (`react.useSuspense` is true, core/i18n.ts:51-53), for a
 * hook that renders no strings of its own. The instance gives the same three
 * things directly.
 *
 * The `languageChanged` subscription is what `useTranslation` was silently
 * providing: without it, `current` would be read once and the active chip would
 * stop moving. Subscribing here keeps the hook true on its own rather than
 * relying on every consumer happening to hold a translation subscription.
 */
export const useChromeLanguage = (): ChromeLanguage => {
  const [current, setCurrent] = useState<SupportedLanguage>(
    () => i18n.language as SupportedLanguage
  );

  useEffect(() => {
    const onChanged = (lng: string) => setCurrent(lng as SupportedLanguage);
    i18n.on("languageChanged", onChanged);
    // The detector can resolve a language between the initial state above and
    // this subscription; React bails out when the value is unchanged.
    setCurrent(i18n.language as SupportedLanguage);
    return () => {
      i18n.off("languageChanged", onChanged);
    };
  }, []);

  // core/i18n.ts:45 sets `supportedLngs: ["sk", "en", "cs"]`, and i18next then
  // APPENDS its debug pseudo-language: `if (options.supportedLngs &&
  // options.supportedLngs.indexOf('cimode') < 0) options.supportedLngs =
  // options.supportedLngs.concat(['cimode'])` (i18next/dist/cjs/i18next.js
  // :1870-1871, personally read at commit time). Rendering the raw array would
  // put a "CIMODE" chip in the drawer, so it is filtered out here — the ONE
  // thing this list needs on top of the instance's own answer.
  const configured: readonly string[] = i18n.options.supportedLngs || [];
  const languages = configured.filter(
    (l): l is SupportedLanguage => l !== "cimode"
  );

  return {
    current,
    languages,
    select: (lng: SupportedLanguage) => {
      if (lng === current) return;
      i18n.changeLanguage(lng);
      customersApi.updateLanguage(lng).catch(() => {
        // silent — see the note above.
      });
    },
  };
};

export interface ChromeIdentity {
  /** Absent until the account loads; both consumers render nothing without it. */
  user?: User;
  /** Avatar letter, "?" when the name is missing. */
  initial: string;
  /** "Role · Company", or the role alone — see the note below. */
  subLine: string;
}

/**
 * The identity block behind the desktop user menu AND the drawer's header.
 *
 * >>> ONE DERIVATION, BECAUSE IT CARRIES AN OPEN OBLIGATION. <<< The company
 * name is loaded by the app shell; while it is absent we show the role alone
 * rather than a placeholder. M2.12 (the nav promotion) must make this
 * SELF-SUFFICIENT if the legacy shell stops fetching `customer` — and that fix
 * must not have to be found and applied in two files, which is exactly what a
 * third verbatim copy of these four lines would have guaranteed.
 *
 * `common` is bound here because the role LABEL is a translation; both callers
 * already bind it (it is also `defaultNS`), so this adds no namespace to
 * either. That is the difference from useChromeLanguage above, which rendered
 * no strings at all.
 */
export const useChromeIdentity = (): ChromeIdentity => {
  const { translate } = useTranslation("common");
  const user = useAppSelector((s: any) => s.account?.user) as User | undefined;
  const { customer } = useAppSelector<CustomerSettingsState>((s: any) => s.customerSettings);

  const roleLabel = user?.role ? translate("common:roles." + user.role) : "";
  return {
    user,
    initial: user?.firstName?.charAt(0)?.toUpperCase() || "?",
    subLine: [roleLabel, customer?.name].filter(Boolean).join(" · "),
  };
};
